import AddIcon from '@mui/icons-material/Add';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import UploadIcon from '@mui/icons-material/Upload';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  InputBase,
  MenuItem,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { DateRange, DateRangePicker } from '@mui/x-date-pickers-pro';
import { differenceInDays } from 'date-fns';
import { format } from 'date-fns-tz';
import { useSnackbar } from 'notistack';
import { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { OrderAttrFilter } from 'src/components/dispatch/orderAttrFilter';
import {
  UnassignedDisplayModeSection,
} from 'src/components/dispatch/unassignedDisplay/UnassignedDisplayModeSection';
import { UnassignedDisplayMode } from 'src/components/dispatch/unassignedDisplay/useUnassignedDisplayMode';
import ServiceEndNotice from 'src/components/ServiceEndNotice.component';
import { ShipmentOrderForm } from 'src/components/V2ShipmentOrderForm';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import { TimetableEntity } from 'src/entities/Timetable.entity';
import { buildTruckShiftsPath } from 'src/pages/TruckShifts/truckShiftGrid';

import DisplayMetricToggle, { DisplayMetric } from './DisplayMetricToggle';
import TimetableSaveDialog from './TimetableSaveDialog';

// TODO(Phase 9): ImportContext は /import ページ専用のため、コンビニ配送配車ページからは
// インポート完了イベントをリアルタイム検知できない。
// 将来的には以下のいずれかで実装する:
//   1. インポート完了後に ImportContext.currentStep === 4 を参照できるよう共有 Context 化する
//   2. インポート完了後に URL パラメータや localStorage で完了フラグを渡す
//   3. ポーリングまたは WebSocket でインポート完了を検知し ImportCompletedDialog を表示する
// 現時点では「インポート」ボタンで /import ページへ遷移する最小実装とする。

type Props = {
  startOn: string;
  endOn: string;
  /** その日のダイヤ（log_web_issues#361）。メニューの選択中の印と上書き保存の対象 */
  timetableId: number | null;
  timetables: TimetableEntity[];
  isLoadingTimetables: boolean;
  isApplying: boolean;
  onDateRangeChange: (start: string, end: string) => void;
  /** 一覧の行を押して確認したとき。その日のダイヤは反映 API が書く（FE から PUT しない。設計書 決定 4） */
  onApplyTimetable: (timetableId: number) => void;
  displayMetrics: DisplayMetric[];
  onDisplayMetricsChange: (metrics: DisplayMetric[]) => void;
  /** 表示対象（配送形態・区間種別）の絞り込み。表示項目トグルの中に置く */
  orderAttrFilter: OrderAttrFilter;
  onOrderAttrFilterChange: (next: OrderAttrFilter) => void;
  onNavigate: (path: string) => void;
  visibleColumns: number;
  onVisibleColumnsChange: (v: number) => void;
  /** 自動配車ボタン */
  onAutoDispatch: () => void;
  isAutoDispatchDisabled: boolean;
  autoDispatchTooltip?: string;
  /** DisplayMetricToggle へ素通しする値。任意項目カテゴリの制御に使う */
  customInputFieldNames: string[];
  isLicenseReady: boolean;
  /** DisplayMetricToggle の GA screen_name */
  screenName: string;
  /**
   * 未割当リストの表示単位 (log_web_issues#338 C8)。表示項目トグルの中に置く。
   * 値を持つのは Presenter（同じ値を右カラムの未割当リストへも渡すため）。
   */
  unassignedDisplayMode: UnassignedDisplayMode;
  onUnassignedDisplayModeChange: (next: UnassignedDisplayMode) => void;
};

const ConvenienceDispatchToolbar: FC<Props> = memo(
  ({
    startOn,
    endOn,
    timetableId,
    timetables,
    isLoadingTimetables,
    isApplying,
    onDateRangeChange,
    onApplyTimetable,
    displayMetrics,
    onDisplayMetricsChange,
    orderAttrFilter,
    onOrderAttrFilterChange,
    onNavigate,
    visibleColumns,
    onVisibleColumnsChange,
    onAutoDispatch,
    isAutoDispatchDisabled,
    autoDispatchTooltip,
    customInputFieldNames,
    isLicenseReady,
    screenName,
    unassignedDisplayMode,
    onUnassignedDisplayModeChange,
  }) => {
    // ---- DateRangePicker ----
    const { enqueueSnackbar } = useSnackbar();
    const [selectedRange, setSelectedRange] = useState<DateRange<Date>>([
      startOn ? new Date(startOn) : null,
      endOn ? new Date(endOn) : null,
    ]);

    useEffect(() => {
      setSelectedRange([
        startOn ? new Date(startOn) : null,
        endOn ? new Date(endOn) : null,
      ]);
    }, [startOn, endOn]);

    const handleDateRange = useCallback(
      (range: DateRange<Date>) => {
        setSelectedRange((prev) => [range[0] ?? prev[0], range[1] ?? prev[1]]);
        const start = range[0];
        const end = range[1];
        if (!start || !end) return;
        const diffDays = differenceInDays(end, start);
        if (diffDays < 0) {
          enqueueSnackbar('終了日は開始日よりも過去に設定できません');
          return;
        }
        const startStr = format(start, 'yyyy-MM-dd', { timeZone: 'Asia/Tokyo' });
        const endStr = format(end, 'yyyy-MM-dd', { timeZone: 'Asia/Tokyo' });
        ReactGA.event('select', {
          screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
          button_name: '日付範囲',
          label: `${startStr}~${endStr}`,
        });
        onDateRangeChange(startStr, endStr);
      },
      [enqueueSnackbar, onDateRangeChange]
    );

    // ---- ダイヤメニュー ----
    const menuAnchorRef = useRef<HTMLButtonElement>(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [saveDialogMode, setSaveDialogMode] = useState<'new' | 'overwrite'>('new');

    const handleMenuClose = () => setMenuOpen(false);

    const handleOpenSaveAsNew = () => {
      ReactGA.event('click', { screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES, button_name: 'ダイヤ 新規保存' });
      handleMenuClose();
      setSaveDialogMode('new');
      setSaveDialogOpen(true);
    };

    const handleOpenOverwriteSave = () => {
      ReactGA.event('click', { screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES, button_name: 'ダイヤ 上書き保存' });
      handleMenuClose();
      setSaveDialogMode('overwrite');
      setSaveDialogOpen(true);
    };

    // ---- ダイヤの呼び出し確認ダイアログ ----
    const [loadTarget, setLoadTarget] = useState<TimetableEntity | null>(null);

    const handlePickTimetable = (timetable: TimetableEntity) => {
      ReactGA.event('click', { screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES, button_name: 'ダイヤ 呼び出し', label: timetable.name });
      handleMenuClose();
      setLoadTarget(timetable);
    };

    const handleLoadConfirm = () => {
      const target = loadTarget;
      setLoadTarget(null);
      if (!target) return;
      ReactGA.event('apply', { screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES, button_name: 'ダイヤ 呼び出し', label: target.name });
      // 選択は反映 API が同じ transaction で書く（FE から PUT しない。設計書 決定 4）
      onApplyTimetable(target.id);
    };

    const handleLoadCancel = () => {
      ReactGA.event('close', { screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES, button_name: 'ダイヤ 呼び出し' });
      setLoadTarget(null);
    };

    const formatUpdatedDate = (iso: string) => {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    const currentTimetable = timetableId == null ? null : (timetables.find((t) => t.id === timetableId) ?? null);
    // その日にすでにダイヤがあれば、呼び出しはマスタの変更を当日へ入れ直す「再反映」になる（log_web_issues#401）
    const isReapply = timetableId != null;

    // ---- 案件作成（V3Plans と同じ ShipmentOrderForm ダイアログ） ----
    const [newShipmentDialogOpen, setNewShipmentDialogOpen] = useState(false);

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        {/* 日付 */}
        <DateRangePicker
          value={selectedRange}
          onChange={handleDateRange}
          slotProps={{ textField: { size: 'small', sx: { p: 0, m: 0 } } }}
        />

        {/* 勤務計画 (V3Plans の ActionsPresenter#shiftButtonMemo を踏襲) */}
        <ServiceEndNotice>
          <Button
            variant="outlined"
            onClick={() => {
              if (!startOn) return;
              ReactGA.event('click', {
                screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                button_name: '勤務計画',
              });
              // 戻り先を convenience-dispatches に指示。Shifts/Presenter#closeButtonOnClick で分岐対応済み
              onNavigate(`/shifts/${startOn}/${endOn || startOn}/convenience-dispatches`);
            }}
            disabled={!startOn}
            sx={{ borderRadius: '5px', height: '35px', p: 1.5 }}
          >
            <Typography variant="body1" sx={{ whiteSpace: 'nowrap', fontSize: '95%' }}>
              勤務計画
            </Typography>
          </Button>
        </ServiceEndNotice>

        {/* 稼働表 (/truck-shifts) — 勤務計画の隣 */}
        <Button
          variant="outlined"
          onClick={() => {
            if (!startOn) return;
            ReactGA.event('click', {
              screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
              button_name: '稼働表',
            });
            // 呼び出し元の範囲をそのまま渡す。稼働表側で週/月へ正規化し「戻る」でこの範囲へ戻す。
            onNavigate(buildTruckShiftsPath({ startOn, endOn: endOn || startOn, backTo: 'convenience-dispatches' }));
          }}
          disabled={!startOn}
          sx={{ borderRadius: '5px', height: '35px', p: 1.5 }}
        >
          <Typography variant="body1" sx={{ whiteSpace: 'nowrap', fontSize: '95%' }}>
            稼働表
          </Typography>
        </Button>

        <Box sx={{ flex: 1 }} />

        {/* 右側ボタン群 */}
        <Stack direction="row" spacing={0.75} alignItems="center">

          {/* ダイヤ ▾ */}
          <Button
            ref={menuAnchorRef}
            data-timetable-trigger
            size="small"
            startIcon={<AltRouteIcon sx={{ fontSize: 14 }} />}
            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 14, opacity: 0.5 }} />}
            onClick={() => {
              ReactGA.event('click', { screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES, button_name: 'ダイヤ' });
              setMenuOpen(true);
            }}
            sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}
          >
            ダイヤ
          </Button>
          <Popover
            anchorEl={menuAnchorRef.current}
            open={menuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { width: 320, maxHeight: '80vh', overflowY: 'auto' } } }}
          >
            {/* 呼び出しセクション */}
            <Box sx={{ px: 1.5, py: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: '0.05em' }}>
                呼び出し
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', fontSize: 10, color: 'text.secondary', mt: 0.25 }}>
                ダイヤのコースの並び・車両構成を土台として読み込み
              </Typography>
            </Box>
            {isLoadingTimetables ? (
              <Box sx={{ px: 2, py: 1, fontSize: 12, color: 'text.secondary' }}>読み込み中...</Box>
            ) : timetables.length === 0 ? (
              <Box sx={{ px: 2, py: 1, fontSize: 12, color: 'text.secondary' }}>登録済みのダイヤはありません</Box>
            ) : (
              timetables.map((timetable) => {
                const selected = timetable.id === timetableId;
                return (
                  <MenuItem
                    key={timetable.id}
                    onClick={() => handlePickTimetable(timetable)}
                    disabled={!startOn || isApplying}
                    aria-current={selected ? 'true' : undefined}
                    sx={{ mx: 0.5, borderRadius: 1, py: 1 }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                      {selected
                        ? <CheckIcon sx={{ fontSize: 14, color: 'primary.main', flexShrink: 0 }} />
                        : <DownloadIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />}
                      <Stack sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 500 }} noWrap>
                          {timetable.name}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>
                          {`コース数: ${timetable.courseCount} ／ 更新日: ${formatUpdatedDate(timetable.updatedAt)}`}
                        </Typography>
                      </Stack>
                    </Stack>
                  </MenuItem>
                );
              })
            )}

            <Divider sx={{ my: 0.5 }} />

            {/* 保存するセクション */}
            <Box sx={{ px: 1.5, py: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: '0.05em' }}>
                保存する
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', fontSize: 10, color: 'text.secondary', mt: 0.25 }}>
                現在の配車計画をダイヤとして保存
              </Typography>
            </Box>
            <MenuItem onClick={handleOpenSaveAsNew} disabled={!startOn} sx={{ mx: 0.5, borderRadius: 1, gap: 1 }}>
              <SaveOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="body2">新規保存</Typography>
            </MenuItem>
            <MenuItem onClick={handleOpenOverwriteSave} disabled={!startOn || timetableId == null} sx={{ mx: 0.5, borderRadius: 1, gap: 1 }}>
              <SaveOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="body2">上書き保存</Typography>
            </MenuItem>

            <Divider sx={{ my: 0.5 }} />

            {/* 名前変更・削除・コースの編集はダイヤ・コースマスタ画面で行う（設計書 決定 3） */}
            <MenuItem
              onClick={() => {
                ReactGA.event('click', { screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES, button_name: 'ダイヤ・コースマスタで編集' });
                handleMenuClose();
                onNavigate('/courses');
              }}
              sx={{ mx: 0.5, borderRadius: 1, gap: 1, mb: 0.5 }}
            >
              <OpenInNewIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="body2">ダイヤ・コースマスタで編集</Typography>
            </MenuItem>
          </Popover>

          {/* 自動配車: 車両と案件の両方が選択されているときに有効 */}
          <Button
            size="small"
            startIcon={<LocalShippingIcon sx={{ fontSize: 14 }} />}
            onClick={() => {
              ReactGA.event('click', {
                screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                button_name: '自動配車',
              });
              onAutoDispatch();
            }}
            disabled={isAutoDispatchDisabled}
            title={autoDispatchTooltip}
            // 押せる状態を色で示す（/map の MapToolbar と同じ規則）。無効時まで
            // contained にすると、押せないのに主要操作に見える
            variant={isAutoDispatchDisabled ? 'text' : 'contained'}
            sx={{
              textTransform: 'none',
              fontSize: 12,
              ...(isAutoDispatchDisabled ? { color: 'text.secondary' } : {}),
            }}
          >
            自動配車
          </Button>

          {/* インポート */}
          <Button
            size="small"
            startIcon={<UploadIcon sx={{ fontSize: 14 }} />}
            onClick={() => {
              ReactGA.event('click', {
                screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                button_name: 'インポート',
              });
              onNavigate('/import');
            }}
            sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}
          >
            インポート
          </Button>

          {/* + 案件作成（V3Plans と同じ ShipmentOrderForm） */}
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={() => {
              ReactGA.event('click', {
                screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                button_name: '案件作成',
              });
              setNewShipmentDialogOpen(true);
            }}
            sx={{ textTransform: 'none', fontSize: 12 }}
          >
            案件作成
          </Button>

          {/* 表示設定の区切り */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ ml: 1, pl: 1.5, borderLeft: 1, borderColor: 'divider' }}
          >
            <DisplayMetricToggle
              value={displayMetrics}
              onChange={onDisplayMetricsChange}
              orderAttrFilter={orderAttrFilter}
              onOrderAttrFilterChange={onOrderAttrFilterChange}
              customInputFieldNames={customInputFieldNames}
              isLicenseReady={isLicenseReady}
              screenName={screenName}
              showCardMetrics
              extras={(
                <UnassignedDisplayModeSection
                  value={unassignedDisplayMode}
                  onChange={onUnassignedDisplayModeChange}
                  screenName={screenName}
                />
              )}
            />
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Typography
                variant="caption"
                sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}
              >
                表示列数
              </Typography>
              <InputBase
                type="number"
                inputProps={{ min: 2, max: 30 }}
                value={visibleColumns}
                onChange={(e) => {
                  const next = Math.max(2, Math.min(30, Number(e.target.value) || 2));
                  ReactGA.event('change', {
                    screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                    button_name: '表示列数',
                    label: String(next),
                  });
                  onVisibleColumnsChange(next);
                }}
                sx={{
                  width: 48,
                  height: 28,
                  px: 0.75,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  fontSize: 12,
                  '& input': { textAlign: 'center', p: 0 },
                }}
              />
            </Stack>
          </Stack>
        </Stack>

        {/* ---- ダイアログ群 ---- */}

        {/* 案件作成フォーム（V3Plans と同じ） */}
        {startOn && newShipmentDialogOpen && (
          <ShipmentOrderForm
            baseDate={new Date(startOn)}
            startOn={startOn}
            newShipmentDialogOpen={newShipmentDialogOpen}
            setNewShipmentDialogOpen={setNewShipmentDialogOpen}
          />
        )}

        <TimetableSaveDialog
          open={saveDialogOpen}
          onClose={() => setSaveDialogOpen(false)}
          planningDate={startOn}
          currentTimetable={currentTimetable}
          mode={saveDialogMode}
        />

        {/* ダイヤ呼び出し確認ダイアログ */}
        <Dialog open={loadTarget != null} onClose={handleLoadCancel} maxWidth="sm" fullWidth aria-labelledby="timetable-load-title">
          <DialogTitle id="timetable-load-title">{isReapply ? 'ダイヤの再反映' : 'ダイヤの呼び出し'}</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5}>
              <DialogContentText>
                {isReapply
                  ? `${startOn} の配車計画を、最新のダイヤ（コースマスタ）で組み直します。出荷データとの紐づけは行いません。`
                  : `選択したダイヤのコースの並び・車両構成を土台として、${startOn} の配車計画に読み込みます。出荷データとの紐づけは行いません。`}
              </DialogContentText>
              {loadTarget && (
                <Box sx={{ borderRadius: 1, bgcolor: 'action.hover', px: 1.5, py: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{loadTarget.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {`コース数: ${loadTarget.courseCount} ／ 更新日: ${formatUpdatedDate(loadTarget.updatedAt)}`}
                  </Typography>
                </Box>
              )}
              <Box sx={{ borderRadius: 1, bgcolor: 'error.light', color: 'error.contrastText', px: 1.5, py: 1 }}>
                <Typography variant="caption">
                  ⚠ 既存の配車計画を1から組み換えることになります。現在の出荷データはすべて未割り当てに戻され、ダイヤに無い勤務は消えます。
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                ※ 後から「ダイヤに対応づけ」で出荷データを紐づけることができます。
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleLoadCancel}>キャンセル</Button>
            <Button variant="contained" onClick={handleLoadConfirm}>{isReapply ? '組み直す' : '読み込み'}</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
);

export default ConvenienceDispatchToolbar;
